import { defineStore } from 'pinia'

export const useFinancialTogglesStore = defineStore('financialToggles', {
  state: () => ({
    fieldToggles: {
      currency_code: true,
      currency_symbol: true,
      tax_rate: true,
      discount_percentage: true,
      min_order_value: true,
      free_shipping_threshold: true,
      shipping_cost: true
    },
    masterToggle: true,
    // Stores the original state of the toggles for MANAGER users
    readOnlyToggles: {
      currency_code: true,
      currency_symbol: true,
      tax_rate: true,
      discount_percentage: true,
      min_order_value: true,
      free_shipping_threshold: true,
      shipping_cost: true,
      masterToggle: true
    }
  }),

  actions: {
    setFieldToggle(field, value) {
      // Rule 1: If enabling any individual toggle, the master toggle must also be enabled
      if (value === true) {
        this.masterToggle = true

        // Rule 2: If enabling any individual toggle, currency_code and currency_symbol must also be enabled
        this.fieldToggles.currency_code = true
        this.fieldToggles.currency_symbol = true
      }

      // Rule 3: If disabling currency_code or currency_symbol, both must be disabled and the master toggle as well
      if ((field === 'currency_code' || field === 'currency_symbol') && value === false) {
        this.fieldToggles.currency_code = false
        this.fieldToggles.currency_symbol = false

        // Disables the master toggle, which in turn will disable all the other individual toggles
        this.setMasterToggle(false)
      } else {
        // For other fields, just updates the value normally
        this.fieldToggles[field] = value
      }

      // Rule 4: If all individual toggles are disabled, the master toggle must also be disabled
      if (Object.values(this.fieldToggles).every(toggle => toggle === false)) {
        this.masterToggle = false
      }
    },

    setMasterToggle(value) {
      this.masterToggle = value

      // Rule 1: If the master toggle is being enabled, all individual toggles must be enabled
      if (value === true) {
        this.setAllTogglesOnly(true)
      }

      // Rule 2: If the master toggle is being disabled, all individual toggles must be disabled
      if (value === false) {
        this.setAllTogglesOnly(false)
      }
    },

    // Method to set all toggles without changing the masterToggle
    setAllTogglesOnly(value) {
      // Creates a new object to ensure reactivity
      const newToggles = {}
      Object.keys(this.fieldToggles).forEach(key => {
        // If the value is true, all toggles are enabled
        // If the value is false, all toggles are disabled
        newToggles[key] = value
      })
      // Updates all toggles at once
      this.fieldToggles = newToggles
    },

    // Original method that sets all toggles and the masterToggle
    setAllToggles(value) {
      this.setAllTogglesOnly(value)
      this.masterToggle = value
    },

    // Updates the masterToggle based on the state of the individual toggles
    updateMasterToggleState() {
      // Directly checks if all toggles are disabled
      const allDisabled = Object.values(this.fieldToggles).every(value => value === false)

      if (allDisabled) {
        console.log('All toggles are disabled, setting master toggle to false')
        this.masterToggle = false
      } else {
        // If at least one toggle is enabled, the masterToggle must be enabled
        console.log('Some toggles are enabled, setting master toggle to true')
        this.masterToggle = true
      }
    },

    loadTogglesFromBackend(data) {
      // Update the toggles based on the backend data
      if (data) {
        console.log('📥 Loading toggles from backend:', data)

        // Update the individual toggles
        if (data.currency_code_enabled !== undefined) this.fieldToggles.currency_code = data.currency_code_enabled
        if (data.currency_symbol_enabled !== undefined) this.fieldToggles.currency_symbol = data.currency_symbol_enabled
        if (data.tax_rate_enabled !== undefined) this.fieldToggles.tax_rate = data.tax_rate_enabled
        if (data.discount_percentage_enabled !== undefined) this.fieldToggles.discount_percentage = data.discount_percentage_enabled
        if (data.min_order_value_enabled !== undefined) this.fieldToggles.min_order_value = data.min_order_value_enabled
        if (data.free_shipping_threshold_enabled !== undefined) this.fieldToggles.free_shipping_threshold = data.free_shipping_threshold_enabled
        if (data.shipping_cost_enabled !== undefined) this.fieldToggles.shipping_cost = data.shipping_cost_enabled

        // Update the main toggle
        if (data.master_toggle_enabled !== undefined) this.masterToggle = data.master_toggle_enabled

        console.log('✅ Toggles loaded successfully:', {
          masterToggle: this.masterToggle,
          fieldToggles: { ...this.fieldToggles }
        })

        // Save the original state for MANAGER users
        this.saveReadOnlyState()
      }
    },

    saveReadOnlyState() {
      // Copies the toggle states to the read-only version
      Object.keys(this.fieldToggles).forEach(key => {
        this.readOnlyToggles[key] = this.fieldToggles[key]
      })
      this.readOnlyToggles.masterToggle = this.masterToggle
    },

    restoreReadOnlyState() {
      // Restores the original toggle states for MANAGER users
      Object.keys(this.fieldToggles).forEach(key => {
        this.fieldToggles[key] = this.readOnlyToggles[key]
      })
      this.masterToggle = this.readOnlyToggles.masterToggle
    },

    prepareTogglesForSubmit() {
      // Prepares the toggle data to send to the backend
      return {
        currency_code_enabled: this.fieldToggles.currency_code,
        currency_symbol_enabled: this.fieldToggles.currency_symbol,
        tax_rate_enabled: this.fieldToggles.tax_rate,
        discount_percentage_enabled: this.fieldToggles.discount_percentage,
        min_order_value_enabled: this.fieldToggles.min_order_value,
        free_shipping_threshold_enabled: this.fieldToggles.free_shipping_threshold,
        shipping_cost_enabled: this.fieldToggles.shipping_cost,
        master_toggle_enabled: this.masterToggle
      }
    }
  },

  getters: {
    isFieldEnabled: (state) => (field) => {
      return state.fieldToggles[field] || false
    },

    areAllEnabled: (state) => {
      return Object.values(state.fieldToggles).every(value => value === true)
    },

    areAllDisabled: (state) => {
      return Object.values(state.fieldToggles).every(value => value === false)
    },

    hasAnyEnabled: (state) => {
      return Object.values(state.fieldToggles).some(value => value === true)
    }
  }
})
